import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, ArrowRight, ChevronDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useFormatPrice, useCurrency } from "@/contexts/CurrencyContext";
import { getRoomPricing, formatPriceLine } from "@/lib/hotelPricing";
import { getAccomTerms, isBivouac } from "@/lib/propertyType";
import BoardOptionsDropdown from "@/components/BoardOptionsDropdown";

const pick = (obj, lang) => (typeof obj === "string" ? obj : obj?.[lang] || obj?.en || "");

// Editorial Room Rates strip on each hotel detail page. Each row is an
// accordion header — tapping it reveals the four board-plan prices via
// the shared BoardOptionsDropdown. Only one row is open at a time so the
// strip stays a clean, scannable comparison table.
const RoomRatesStrip = ({ rooms = [], hotelSlug }) => {
  const { lang } = useLanguage();
  const { fromMAD } = useFormatPrice();
  const { currency } = useCurrency();
  const [openIdx, setOpenIdx] = useState(null);

  const pricedRooms = useMemo(
    () =>
      (rooms || [])
        .map((r) => {
          const pricing = getRoomPricing(r, hotelSlug);
          return {
            room: r,
            pricing,
            line: formatPriceLine(pricing, lang, fromMAD),
          };
        })
        .filter((x) => x.line && x.line.amount)
        .slice(0, 7),
    [rooms, hotelSlug, lang, fromMAD]
  );

  const terms = getAccomTerms(hotelSlug, lang);
  const camp = isBivouac(hotelSlug);

  if (pricedRooms.length === 0) return null;
  const lowest = pricedRooms[0]?.line?.amount;

  return (
    <section
      className="py-16 md:py-20 bg-[#F2EBE1]/40 border-b border-[#2C2621]/10"
      data-testid="room-rates-strip"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="bg-[#FDFBF7] border border-[#E8DFCF]">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
            <div className="md:col-span-4 bg-[#1A1513] text-[#FDFBF7] p-8 md:p-10 flex flex-col justify-between">
              <div>
                <p className="overline !text-[#D4A373] flex items-center gap-2">
                  <BedDouble className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {terms.rates_title}
                </p>
                <h3 className="font-serif-x mt-3 text-3xl md:text-4xl leading-[1.05]">
                  {pick(
                    {
                      en: `${terms.public_rates} from ${lowest}`,
                      fr: `${terms.public_rates} dès ${lowest}`,
                      es: `${terms.public_rates} desde ${lowest}`,
                    },
                    lang
                  )}
                </h3>
                <p className="mt-4 text-sm text-[#FDFBF7]/70 leading-relaxed">
                  {camp
                    ? pick(
                        {
                          en: "Per tent · per night · breakfast included. Half-board, full-board and agency contracts on request.",
                          fr: "Par tente · par nuit · petit-déjeuner inclus. Demi-pension, pension complète et tarifs agence sur demande.",
                          es: "Por tienda · por noche · desayuno incluido. Media pensión, pensión completa y tarifas de agencia bajo petición.",
                        },
                        lang
                      )
                    : pick(
                        {
                          en: "Per room · per night · breakfast included. Half-board, full-board and agency contracts on request.",
                          fr: "Par chambre · par nuit · petit-déjeuner inclus. Demi-pension, pension complète et tarifs agence sur demande.",
                          es: "Por habitación · por noche · desayuno incluido. Media pensión, pensión completa y tarifas de agencia bajo petición.",
                        },
                        lang
                      )}
                </p>
                <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-[#D4A373]/80">
                  {camp
                    ? pick(
                        {
                          en: "Tap any tent to compare boards",
                          fr: "Touchez une tente pour comparer",
                          es: "Toca una tienda para comparar",
                        },
                        lang
                      )
                    : pick(
                        {
                          en: "Tap any room to compare boards",
                          fr: "Touchez une chambre pour comparer",
                          es: "Toca una habitación para comparar",
                        },
                        lang
                      )}
                </p>
              </div>
              <Link
                to={`/booking?hotel=${encodeURIComponent(hotelSlug)}`}
                className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D4A373] hover:text-[#FDFBF7] w-fit"
                data-testid="room-rates-book"
              >
                {camp
                  ? pick({ en: "Reserve a tent", fr: "Réserver une tente", es: "Reservar una tienda" }, lang)
                  : pick({ en: "Reserve a room", fr: "Réserver une chambre", es: "Reservar una habitación" }, lang)}
                <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            </div>
            <div className="md:col-span-8 p-6 md:p-8">
              <ul className="divide-y divide-[#E8DFCF]" data-testid="room-rates-list">
                {pricedRooms.map(({ room, pricing, line }, i) => {
                  const isOpen = openIdx === i;
                  const rowKey = `rate-${pick(room.name, "en") || (typeof room.surface === "string" ? room.surface : pick(room.surface, "en")) || i}`;
                  return (
                    <li key={rowKey} className="py-2">
                      <button
                        type="button"
                        onClick={() => setOpenIdx(isOpen ? null : i)}
                        aria-expanded={isOpen}
                        className={`group w-full flex items-baseline gap-3 sm:gap-4 py-2.5 md:py-3 text-left transition-colors ${
                          isOpen ? "text-[#2C2621]" : "hover:text-[#2C2621]"
                        }`}
                        data-testid={`room-rates-row-${i}`}
                      >
                        <ChevronDown
                          className={`w-3.5 h-3.5 shrink-0 self-center text-[#A07042] transition-transform duration-300 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          strokeWidth={1.8}
                        />
                        <span className="font-serif-x text-base md:text-lg text-[#2C2621] flex-1 leading-snug">
                          {pick(room.name, lang)}
                        </span>
                        <span className="text-xs tracking-[0.2em] uppercase text-[#5C5248]/70 hidden sm:inline">
                          {pick(room.surface, lang)}
                        </span>
                        <span className="text-sm md:text-base font-medium text-[#C16542] tabular-nums whitespace-nowrap min-w-[9rem] text-right">
                          <span className="block text-[9px] tracking-[0.2em] uppercase text-[#5C5248]/70 font-normal">
                            {pick({ en: "From", fr: "À partir de", es: "Desde" }, lang)}
                          </span>
                          {pricing?.perPerson?.fromMAD != null
                            ? fromMAD(pricing.perPerson.fromMAD)
                            : line.amount}
                          <span className="block text-[9px] tracking-[0.15em] text-[#5C5248]/70 font-normal">
                            {pick({ en: "per person / night", fr: "par pers. / nuit", es: "por persona / noche" }, lang)}
                          </span>
                        </span>
                      </button>
                      {pricing?.plans && (
                        <div
                          className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                            isOpen ? "grid-rows-[1fr] pb-3" : "grid-rows-[0fr]"
                          }`}
                          aria-hidden={!isOpen}
                        >
                          <div className="overflow-hidden">
                            <div className="pt-2 pl-6 sm:pl-7">
                              <BoardOptionsDropdown
                                plans={pricing.perPerson?.plans || pricing.plans}
                                occupancy={pricing.perPerson?.occupancy}
                                lang={lang}
                                testid={`room-rates-board-${i}`}
                                variant="light"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-xs text-[#5C5248]/70 italic">
                {pick(
                  {
                    en: `Prices in ${currency} · indicative public rates · final price confirmed by the concierge.`,
                    fr: `Tarifs en ${currency} · prix publics indicatifs · prix final confirmé par la conciergerie.`,
                    es: `Tarifas en ${currency} · precios públicos indicativos · precio final confirmado por la conserjería.`,
                  },
                  lang
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoomRatesStrip;
